import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    Pressable,
    Animated,
    Platform,
    Easing,
    View
} from "react-native";
import type IBadgeIndicatorProps from "./type";
import type {
    BadgeIndicatorWebPropsType
} from "./type";
import stylesheet, {
    BADGE_INDICATOR_MAX_COUNT,
    getBadgeIndicatorColors,
    getBadgeIndicatorCount,
    getBadgeIndicatorMark,
    useStyles
} from "./stylesheet";
import {
    BadgeIndicatorAnchorContext
} from "./context";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    MOTION_BADGE_HIDDEN_SCALE,
    getMotionDuration,
    useReducedMotion,
    MOTION_EASINGS
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    BadgeCounterDirection
} from "../../internal/counter/type";
import {
    splitControlStyle
} from "../../../../helpers/theme/control";
import {
    formatNumber
} from "../../../../helpers/locale/format";
import {
    warnOnce
} from "../../../../utils/development";
import BadgeCounter from "../../internal/counter";
import StateLayer from "../../../stateLayer";
import Loading from "../../../loading";

const BADGE_ENTER_EASING = Easing.out(Easing.back(1.7));

const BadgeIndicator: FC<IBadgeIndicatorProps> = ({
    floorBackgroundColor: floorBackgroundColorProp,
    max = BADGE_INDICATOR_MAX_COUNT,
    titleVariant = "labelSmallSize",
    spreadBehaviour = "baseline",
    "aria-label": ariaLabel,
    accessibilityLabel,
    isDisabled = false,
    isLoading = false,
    isVisible = true,
    type = "neutral",
    customLocalize,
    customTheme,
    titleStyle,
    children,
    location,
    onFocus,
    onPress,
    onBlur,
    count,
    style,
    role,
    ...props
}) => {
    const {
        shapeConfigs,
        surfaceColor,
        touchTargets,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const floorBackgroundColor = floorBackgroundColorProp ?? surfaceColor;

    const {
        activeLocale,
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const [
        anchorInset,
        setAnchorInset
    ] = useState<number | null>(null);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onFocus,
        onBlur
    });

    const isPressable = !!onPress;

    const mark = getBadgeIndicatorMark({
        isLoading,
        isVisible,
        count
    });

    const countTitle = mark === "count" && count !== undefined ? getBadgeIndicatorCount({
        locale: activeLocale,
        count,
        max
    }) : undefined;

    const [
        displayedMark,
        setDisplayedMark
    ] = useState(mark);

    const presence = useRef(new Animated.Value(mark === "none" ? 0 : 1)).current;

    const lastCountTitleRef = useRef(countTitle);

    const previousCountRef = useRef(count);

    if(countTitle !== undefined) {
        lastCountTitleRef.current = countTitle;
    }

    const getCounterDirection = (): BadgeCounterDirection => {
        const previousCount = previousCountRef.current;

        if(count === undefined || previousCount === undefined || count === previousCount) {
            return 0;
        }

        return count > previousCount ? 1 : -1;
    };

    const counterDirection = getCounterDirection();

    const presenceStyle = useMemo(() => ({
        transform: [
            {
                scale: presence.interpolate({
                    outputRange: [
                        MOTION_BADGE_HIDDEN_SCALE,
                        1
                    ],
                    inputRange: [
                        0,
                        1
                    ]
                })
            }
        ],
        opacity: presence.interpolate({
            extrapolate: "clamp",
            outputRange: [
                0,
                1
            ],
            inputRange: [
                0,
                1
            ]
        })
    }), [
        presence
    ]);

    useEffect(() => {
        previousCountRef.current = count;
    }, [
        count
    ]);

    useEffect(() => {
        const isEntering = mark !== "none";

        if(isEntering) {
            setDisplayedMark(mark);
        }

        const duration = getMotionDuration({
            duration: isEntering ? "medium" : "short",
            isReduceMotionEnabled,
            durations
        });

        if(!duration) {
            presence.setValue(isEntering ? 1 : 0);

            if(!isEntering) {
                setDisplayedMark("none");
            }

            return;
        }

        const animation = Animated.timing(presence, {
            easing: isEntering ? BADGE_ENTER_EASING : MOTION_EASINGS.exit,
            useNativeDriver: Platform.OS !== "web",
            toValue: isEntering ? 1 : 0,
            duration
        });

        animation.start(({
            finished
        }) => {
            if(finished && !isEntering) {
                setDisplayedMark("none");
            }
        });

        return () => {
            animation.stop();
        };
    }, [
        mark
    ]);

    const currentColors = getBadgeIndicatorColors({
        isDisabled,
        colors,
        type
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        mark: markDynamicStyle
    } = useStyles({
        floorBackgroundColor,
        mark: displayedMark,
        userStyle: style,
        spreadBehaviour,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        titleVariant,
        touchTargets,
        anchorInset,
        isPressable,
        pointerType,
        isDisabled,
        typography,
        direction,
        isLoading,
        location,
        radiuses,
        borders,
        colors,
        spaces
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const surfaceWebProps: BadgeIndicatorWebPropsType = Platform.OS === "web" ? {
        dir: direction
    } : {};

    useEffect(() => {
        if(!isPressable || ariaLabel || accessibilityLabel) {
            return;
        }

        warnOnce({
            message: "Badge.Indicator: a pressable indicator needs an \"accessibilityLabel\" (or \"aria-label\"); the badge is hidden from screen readers and its count is added to that label."
        });
    }, [
        accessibilityLabel,
        isPressable,
        ariaLabel
    ]);

    const getAccessibilityLabel = () => {
        const label = ariaLabel ?? accessibilityLabel;

        if(!isPressable || !label) {
            return label;
        }

        if(mark === "dot") {
            return localize("notification-indicator-new", [
                label
            ]);
        }

        if(!countTitle || count === undefined) {
            return label;
        }

        if(count > max) {
            return localize("notification-indicator-overflow", [
                label,
                formatNumber({
                    locale: activeLocale,
                    value: max
                })
            ]);
        }

        return localize("notification-indicator-count", [
            label,
            countTitle
        ]);
    };

    const renderMarkContent = () => {
        if(displayedMark === "loading") {
            return <Loading
                size={iconSizes.small - shapeConfigs.chipIconOffset}
                customColor={currentColors.titleColor}
                customTheme={customTheme}
            />;
        }

        const currentCountTitle = countTitle ?? lastCountTitleRef.current;

        if(displayedMark !== "count" || !currentCountTitle) {
            return null;
        }

        return <BadgeCounter
            customColor={currentColors.titleColor}
            direction={counterDirection}
            titleVariant={titleVariant}
            customTheme={customTheme}
            title={currentCountTitle}
            titleStyle={titleStyle}
        />;
    };

    const renderMark = () => {
        if(displayedMark === "none") {
            return null;
        }

        return <Animated.View
            style={[
                stylesheet.mark,
                markDynamicStyle,
                presenceStyle
            ]}
            aria-hidden={true}
        >
            {renderMarkContent()}
        </Animated.View>;
    };

    if(!isPressable) {
        return <View
            {...props}
            {...surfaceWebProps}
            accessibilityLabel={accessibilityLabel}
            style={[
                style,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
            aria-label={ariaLabel}
            aria-busy={isLoading}
            onFocus={onFocus}
            onBlur={onBlur}
            role={role}
        >
            <BadgeIndicatorAnchorContext.Provider
                value={setAnchorInset}
            >
                {children}
            </BadgeIndicatorAnchorContext.Provider>
            {renderMark()}
        </View>;
    }

    const currentLabel = getAccessibilityLabel();

    return <Pressable
        {...props}
        {...interactionHandlers}
        onPress={isLoading ? undefined : onPress}
        accessibilityLabel={currentLabel}
        aria-disabled={isDisabled}
        aria-label={currentLabel}
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role={role ?? "button"}
        aria-busy={isLoading}
        disabled={isDisabled}
    >
        <View
            {...surfaceWebProps}
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                isHovered={!isLoading && isHovered}
                isPressed={!isLoading && isPressed}
                borderRadius={radiuses.actions}
                customTheme={customTheme}
            />
            <BadgeIndicatorAnchorContext.Provider
                value={setAnchorInset}
            >
                {children}
            </BadgeIndicatorAnchorContext.Provider>
            {renderMark()}
        </View>
    </Pressable>;
};
export default BadgeIndicator;
